/**
 * AreaTickDialog - who is caught, ticked by hand.
 *
 * Shown when an area weapon is fired while positions do not count. It draws
 * an `AreaAsk` (`engine/phase/areaAsk`): what is fired, its declared size
 * as a guide, and the people who may be caught. Click a row to tick it. An
 * arc is ticked IN THE ORDER the chain reaches people, and each row shows
 * its place. A weapon whose victims set off a second template gets a second
 * list, each tick saying who set it off.
 *
 * The same dialog serves three seats, told apart only by its buttons:
 * the attacker who may rule alone (`fire`), the attacker who must ask the
 * GM (`propose`), and the GM confirming or correcting (`confirm`).
 */
import { useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/AreaTickDialog.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import { setSpawner, toggleCaught, toggleSpawned } from '../engine/phase/areaTicks';

const SUBMIT = {
  fire: 'Fire',
  propose: 'Ask the GM',
  confirm: 'Confirm',
};

/** The primary list. An arc's rows carry their place in the chain. */
function CaughtList({ ask, ticks, setTicks }) {
  const ordered = ask.shape === 'arc';
  return (
    <div className={styles.list} role="listbox" aria-multiselectable="true" aria-label="Who is caught">
      {ask.candidates.map((c) => {
        const place = ticks.caught.indexOf(c.id);
        return (
          <button
            key={c.id}
            type="button"
            role="option"
            aria-selected={place >= 0}
            className={styles.row}
            data-testid={`area-tick-${c.id}`}
            onClick={() => setTicks(toggleCaught(ticks, c.id))}
          >
            <span className={styles.name}>{c.name}</span>
            {ordered && place >= 0 && (
              <span className={styles.place} data-testid={`area-order-${c.id}`}>
                {place === 0 ? 'struck first' : `leap ${place}`}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** One row of the secondary list: tick it, and say who set it off. */
function SpawnedRow({ candidate, ticks, setTicks, nameOf }) {
  const entry = ticks.spawned.find((s) => s.id === candidate.id);
  const first = ticks.caught[0];
  return (
    <div className={styles.spawnRow}>
      <button
        type="button"
        role="option"
        aria-selected={!!entry}
        className={styles.row}
        data-testid={`area-spawn-${candidate.id}`}
        disabled={!first}
        onClick={() => setTicks(toggleSpawned(ticks, candidate.id, first))}
      >
        <span className={styles.name}>{candidate.name}</span>
      </button>
      {entry && (
        <select
          className={styles.select}
          aria-label={`Set off by, for ${candidate.name}`}
          data-testid={`area-spawn-from-${candidate.id}`}
          value={entry.from}
          onChange={(e) => setTicks(setSpawner(ticks, candidate.id, e.target.value))}
        >
          {ticks.caught.map((id) => (
            <option key={id} value={id}>
              set off by {nameOf(id)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** The secondary template: everyone it may catch who is not already caught by the first. */
function SpawnedList({ ask, ticks, setTicks }) {
  const nameOf = (id) => ask.candidates.find((c) => c.id === id)?.name ?? id;
  const free = ask.spawn.candidates.filter((c) => !ticks.caught.includes(c.id));
  return (
    <section className={styles.section}>
      <h4 className={styles.caption}>Set off by those caught</h4>
      <p className={styles.guide}>{ask.spawn.guide}</p>
      <div
        className={styles.list}
        role="listbox"
        aria-multiselectable="true"
        aria-label="Who the second template catches"
      >
        {free.length === 0 && <p className={styles.empty}>Nobody else it could catch.</p>}
        {free.map((c) => (
          <SpawnedRow key={c.id} candidate={c} ticks={ticks} setTicks={setTicks} nameOf={nameOf} />
        ))}
      </div>
    </section>
  );
}

/**
 * @param {object} props
 * @param {import('../engine/phase/areaAsk').AreaAsk} props.ask
 * @param {'fire'|'propose'|'confirm'} props.mode
 * @param {(ticks: import('../engine/phase/areaTicks').AreaTicks) => void} props.onSubmit
 * @param {() => void} props.onCancel
 */
export default function AreaTickDialog({ ask, mode, onSubmit, onCancel }) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(onCancel, trapRef);
  useEscapeKey(true, onCancel); // Escape cancels, as a click outside does (2026-10-23).
  const [ticks, setTicks] = useState(ask.ticks);
  const count = ticks.caught.length;
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Who is caught"
        data-testid="area-tick-dialog"
      >
        <h3 className={styles.title}>Who is caught?</h3>
        <p className={styles.what}>
          {ask.attacker} · {ask.weapon}
        </p>
        <p className={styles.guide} data-testid="area-tick-guide">
          {ask.guide}
        </p>
        {ask.scatter && (
          <p className={styles.scatter} data-testid="area-tick-scatter">
            It missed. {ask.scatter}
          </p>
        )}
        <CaughtList ask={ask} ticks={ticks} setTicks={setTicks} />
        {ask.spawn && <SpawnedList ask={ask} ticks={ticks} setTicks={setTicks} />}
        <div className={styles.footer}>
          <span className={styles.count} data-testid="area-tick-count">
            {count === 0 ? 'Nobody is caught' : `${count} caught`}
          </span>
          <button type="button" className={common.btnSecondary} data-testid="area-tick-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={common.btnPrimary}
            data-testid="area-tick-submit"
            onClick={() => onSubmit(ticks)}
          >
            {SUBMIT[mode]}
          </button>
        </div>
      </div>
    </div>
  );
}
